import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategoriesWithSubcategories } from '../../../hooks/useCategoriesWithSubcategories';
import { getCategoryIconType, getCategoryNavigationTarget } from '../../../utils/categoryNav';
import { CategoryIcon } from './CategoryIcons';
import backgroundImage from '../../../assets/images/category.svg';

function ChevronLeft() {
  return (
    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function CategoryCardSkeleton() {
  return (
    <div className="flex h-[151px] w-[145px] shrink-0 animate-pulse flex-col items-center justify-center rounded-lg bg-white/90">
      <div className="h-10 w-10 rounded-full bg-gray-200" />
      <div className="mt-3 h-4 w-16 rounded bg-gray-200" />
    </div>
  );
}

function CategoryCard({ category, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-[151px] w-[145px] shrink-0 flex-col items-center justify-center rounded-lg bg-white shadow-none transition-[border-radius,box-shadow] duration-300 ease-out hover:z-10 hover:rounded-[30px] hover:shadow-[0_10px_20px_rgba(0,0,0,0.2)]"
    >
      <CategoryIcon type={getCategoryIconType(category.name)} />
      <span className="mt-3 text-center text-base font-medium text-[#1B1B1B] transition-all duration-300 sm:text-[18px] group-hover:font-semibold">
        {category.name}
      </span>
    </button>
  );
}

function CategorySection() {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { categories, loading, error } = useCategoriesWithSubcategories();

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = direction === 'left' ? -200 : 200;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const handleCategoryClick = (category) => {
    const target = getCategoryNavigationTarget(category);
    if (!target) return;
    navigate(target.path, { state: target.state });
  };

  const renderCategories = () => {
    if (loading && !categories.length) {
      return (
        <>
          {[1, 2, 3, 4].map((item) => (
            <CategoryCardSkeleton key={item} />
          ))}
        </>
      );
    }

    if (error) {
      return <p className="w-full text-center text-sm text-red-300">{error}</p>;
    }

    if (!categories.length) {
      return <p className="w-full text-center text-sm text-white/70">No categories available right now.</p>;
    }

    return categories.map((category) => (
      <CategoryCard
        key={category.id}
        category={category}
        onClick={() => handleCategoryClick(category)}
      />
    ));
  };

  return (
    <div
      className="relative w-full min-h-[300px] overflow-hidden bg-cover bg-center bg-no-repeat px-4 py-10 sm:px-6 lg:min-h-[334px] lg:px-8 lg:py-16"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-6 xl:gap-8">
        <div className="max-w-md shrink-0 text-white">
          <h2 className="text-3xl font-medium sm:text-4xl lg:text-[36px] xl:text-[36px]">
            Explore{' '}
            <span className="block mt-4 text-4xl font-medium lg:text-[40px] xl:text-[48px]">Our Category</span>
          </h2>

          <p className="mt-5 text-sm leading-relaxed text-[#cccccc] sm:text-base">
            From foundation to mastery, explore courses that fit every learner&apos;s journey.
          </p>
        </div>

        <div className="flex w-full min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition sm:h-12 sm:w-12"
            aria-label="Previous categories"
          >
            <ChevronLeft />
          </button>

          <div
            ref={scrollRef}
            className="flex min-w-0 flex-1 flex-nowrap items-center justify-center gap-4 overflow-x-auto scroll-smooth px-2 py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {renderCategories()}
          </div>

          <button
            type="button"
            onClick={() => scroll('right')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition hover:bg-white/10 sm:h-12 sm:w-12"
            aria-label="Next categories"
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategorySection;
